<template>
    <div v-if="newsletterVisible" ref="newsletterCompact" class="newsletter" :class="modifierClasses">
        <img
            src="~/assets/images/newsletter.svg"
            alt="Newsletter"
            class="newsletter__img"
            :class="imgModifierClasses"
        />
        <div class="newsletter__content">
            <template v-if="!subscribed">
                <h3 id="newsletterTitle" class="newsletter__heading">{{ $t('newsletter.title') }}</h3>
                <p class="newsletter__description">
                    {{ $t('newsletter.description') }}
                    <LocalizedLink :to="{ name: 'privacy-policy' }" target="_blank">
                        {{ $t('newsletter.privacy_policy_link') }}
                    </LocalizedLink>
                </p>
                <form class="newsletter__form" @submit.prevent="subscribe">
                    <template v-if="!isLoggedIn">
                        <div v-for="field in formFields" :key="field.key" class="newsletter__form__input">
                            <label class="label">{{ field.translation }}</label>
                            <input
                                v-model.trim="$v.formData[field.key].$model"
                                type="text"
                                class="input"
                                :class="{
                                    'input--error': $v.formData[field.key].$dirty && $v.formData[field.key].$invalid,
                                }"
                            />
                        </div>
                    </template>
                    <button class="button button--primary" type="submit" :disabled="submitButtonDisabled">
                        <LoadingIndicator v-if="isLoading" />
                        <template v-else>{{ $t('buttons.submit') }}</template>
                    </button>
                </form>
            </template>
            <template v-else>
                <h3 class="newsletter__heading">{{ $t('newsletter.success_title') }}</h3>
                <p class="newsletter__description--successful">{{ $t('newsletter.success_description') }}</p>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import requiredIf from 'vuelidate/lib/validators/requiredIf';
import email from 'vuelidate/lib/validators/email';

import newsletterMixin from '~/mixins/newsletterMixin';
import LoadingIndicator from '~/components/LoadingIndicator.vue';

export default {
    name: 'NewsletterCompact',
    components: {
        LoadingIndicator,
    },
    mixins: [validationMixin, newsletterMixin],
    data() {
        return {
            formData: {
                name: null,
                email: null,
            },
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen', 'isLoggedIn']),
        modifierClasses() {
            return {
                'newsletter--subscribed': this.subscribed,
            };
        },
        imgModifierClasses() {
            return {
                'newsletter__img--subscribed': this.subscribed,
            };
        },
        newsletterVisible() {
            if (this.isLoggedIn && this.user.subscriptions?.newsletter) {
                return false;
            }

            return true;
        },
        formFields() {
            return [
                { key: 'name', translation: this.$t('common.first_name') },
                { key: 'email', translation: this.$t('common.email') },
            ];
        },
        submitButtonDisabled() {
            return this.isLoading || this.$v.$invalid;
        },
    },
    validations() {
        return {
            formData: {
                name: {
                    required: requiredIf(() => {
                        return !this.isLoggedIn;
                    }),
                },
                email: {
                    email,
                    required: requiredIf(() => {
                        return !this.isLoggedIn;
                    }),
                },
            },
        };
    },
    methods: {
        scrollToNewsletterCompact() {
            if (!this.isLargeScreen) {
                this.$refs.newsletterCompact.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        subscribe() {
            this.isLoggedIn ? this.handleLoggedInSubscription() : this.handleGuestSubscription(this.formData);

            this.$nextTick(this.scrollToNewsletterCompact);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

.newsletter {
    background: $white;
    display: flex;
    flex-direction: column;
    padding: 16px;
    justify-content: center;
    align-items: center;
    gap: 40px;
    flex-shrink: 0;

    border-radius: $default-border-radius;
    border: 3px solid $gray-light;

    @include md {
        flex-direction: row;
        justify-content: space-between;
        padding: 16px 48px;
        gap: initial;
    }

    &--subscribed {
        @include md {
            justify-content: flex-start;
            gap: 71px;
        }
    }

    &__img {
        width: 80%;
        height: auto;

        @include md {
            width: auto;
            height: auto;
            max-width: initial;
            margin-right: 16px;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;

        @include md {
            align-items: flex-start;
        }
    }

    &__heading {
        @extend %heading-3;
        text-align: center;

        @include md {
            text-align: left;
        }
    }

    &__description {
        margin: 16px 0 24px;
        text-align: center;

        &--successful {
            margin: 16px 0 0;
            text-align: center;
        }

        @include md {
            text-align: left;
        }
    }

    &__form {
        display: flex;
        width: 100%;
        gap: 16px;
        flex-wrap: wrap;

        &__input {
            width: 100%;
            min-width: 195px;
        }

        button {
            height: fit-content;
            align-self: end;
            flex: 1;
        }

        @include lg {
            &__input {
                width: initial;
                flex: 1;
            }

            button {
                flex: inherit;
            }
        }
    }
}
</style>
